<template>
<page-wrapper>
  <v-progress-linear v-if="!job" indeterminate></v-progress-linear>
  <div v-else class="list-header mb-4">
    <h2>
      <span class="font-weight-regular">{{ job.number }}</span> {{ job.name }}
    </h2>
    <div v-if="regularItems.length" class="summary">
      <v-chip v-if="summary.pulled" small class="ml-2" color="blue lighten-1" dark>{{ summary.pulled }} pulled</v-chip>
      <v-chip v-if="summary.loaded" small class="ml-2" color="indigo darken-1" dark>{{ summary.loaded }} loaded</v-chip>
      <v-chip small class="ml-2" :color="summary.returned === summary.total ? 'green lighten-1' : 'orange lighten-1'" dark>
        {{ summary.returned }} returned
      </v-chip>
    </div>
  </div>

  <div class="add-row">
    <stock-item-select ref="addSelect" v-model="selected" class="item-search"></stock-item-select>
    <v-text-field v-model.number="quantity" label="Qty" type="number"
                  min="1" hide-details class="qty-field"></v-text-field>
    <v-btn color="primary" depressed @click="addItem">Add</v-btn>
  </div>

  <table v-if="loadList.length" class="load-table mt-4">
    <thead>
      <tr>
        <th style="width: 1rem">Qty</th>
        <th>Item</th>
        <th class="desktop-only">Pulled</th>
        <th class="desktop-only">Loaded</th>
        <th class="desktop-only">Returned</th>
        <th></th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <template v-for="(group, gi) in groups">
        <tr v-for="item in group" :key="item.id">
          <template v-if="isSeparator(item)">
            <td colspan="7" class="separator-cell">
              <span class="group-name" @click="openEditGroup(item)">{{ item.name.slice(1) }}</span>
            </td>
          </template>
          <template v-else>
            <td><span class="item" @click="openEdit(item)">{{ item.quantity }}</span></td>
            <td>
              <span class="item" @click="openEdit(item)">{{ item.name }}</span>
            </td>
            <td class="desktop-only">
              <span v-if="item.pulled_at" class="grey--text text--darken-2">
                {{ item.pulled_by }} {{ formatDate(item.pulled_at) }}
              </span>
              <span v-else class="grey--text">—</span>
            </td>
            <td class="desktop-only">
              <span v-if="item.loaded_at" class="grey--text text--darken-2">
                {{ item.loaded_by }} {{ formatDate(item.loaded_at) }}
              </span>
              <span v-else class="grey--text">—</span>
            </td>
            <td class="desktop-only">
              <span v-if="item.returned_at" class="grey--text text--darken-2">
                {{ item.returned_by }} {{ formatDate(item.returned_at) }}
              </span>
              <span v-else class="grey--text">—</span>
            </td>
            <td class="action-col">
              <v-btn v-if="itemStatus(item) === 'none'" depressed small color="primary"
                     @click="pullItem(item)">Pull</v-btn>
              <v-btn v-else-if="itemStatus(item) === 'pulled'" depressed small color="indigo darken-1" dark
                     @click="loadItem(item)">Load</v-btn>
              <v-btn v-else-if="itemStatus(item) === 'loaded'" depressed small color="orange darken-1" dark
                     @click="returnItem(item)">Return</v-btn>
              <v-chip v-else small label outlined color="green lighten-1">RETURNED</v-chip>
            </td>
            <td class="action-col icons-col">
              <v-icon v-if="itemStatus(item) === 'none'" small @click="confirmDelete(item)">mdi-delete</v-icon>
              <v-icon v-else-if="canUndo(item)" small @click="undoItem(item)">mdi-undo</v-icon>
            </td>
          </template>
        </tr>
        <tr :key="'add-' + gi" class="group-add-row">
          <td></td>
          <td></td>
          <td class="desktop-only"></td>
          <td class="desktop-only"></td>
          <td class="desktop-only"></td>
          <td class="action-col"></td>
          <td class="action-col icons-col">
            <v-icon @click="openAddToGroup(group[0].group_num)">mdi-plus</v-icon>
          </td>
        </tr>
      </template>
    </tbody>
  </table>

  <div class="mt-4">
    <v-btn outlined small @click="newGroupDialog = true">
      <v-icon left>mdi-plus</v-icon>New Group
    </v-btn>
  </div>

  <v-dialog v-model="deleteDialog" max-width="320px">
    <v-card>
      <v-card-title>Remove item?</v-card-title>
      <v-card-text>Remove <strong>{{ deleteTarget && deleteTarget.name }}</strong> from the load list?</v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="deleteDialog = false">Cancel</v-btn>
        <v-btn text color="error" @click="removeItem">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="editDialog" max-width="480px">
    <edit-load-list-item :item="editTarget" @close="editDialog = false" @saved="fetchLoadList"></edit-load-list-item>
  </v-dialog>

  <v-dialog v-model="editGroupDialog" max-width="400px">
    <v-card>
      <v-card-title>Edit Group</v-card-title>
      <v-card-text>
        <v-text-field v-model="editGroupName" label="Group name" hide-details
                      @keyup.enter="saveGroupName"></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="editGroupDialog = false">Cancel</v-btn>
        <v-btn text color="primary" @click="saveGroupName">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="addItemDialog" max-width="400px">
    <v-card>
      <v-card-title>Add Item</v-card-title>
      <v-card-text>
        <stock-item-select ref="groupAddSelect" v-model="selected" class="mt-2"></stock-item-select>
        <v-text-field v-model.number="quantity" label="Qty" type="number"
                      min="1" hide-details class="mt-3"></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="addItemDialog = false">Cancel</v-btn>
        <v-btn text color="primary" @click="addToGroup">Add</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="newGroupDialog" max-width="400px">
    <v-card>
      <v-card-title>New Group</v-card-title>
      <v-card-text>
        <v-text-field ref="newGroupInput" v-model="newGroupName" label="Group name" hide-details
                      @keyup.enter="createGroup"></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="newGroupDialog = false">Cancel</v-btn>
        <v-btn text color="primary" @click="createGroup">Create</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</page-wrapper>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import * as api from '../api'
import PageWrapper from '../components/ui/PageWrapper'
import StockItemSelect from '../components/load-list/StockItemSelect'
import EditLoadListItem from '../components/load-list/EditLoadListItem'
import dayjs from 'dayjs'

export default {
  name: 'LoadList',
  components: { PageWrapper, StockItemSelect, EditLoadListItem },
  props: {
    jobId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      selected: null,
      quantity: 1,
      loadList: [],
      deleteDialog: false,
      deleteTarget: null,
      editDialog: false,
      editTarget: null,
      editGroupDialog: false,
      editGroupTarget: null,
      editGroupName: '',
      addItemDialog: false,
      addItemGroupNum: 1,
      newGroupDialog: false,
      newGroupName: ''
    }
  },
  watch: {
    newGroupDialog(val) {
      if (val) setTimeout(() => this.$refs.newGroupInput.focus(), 100)
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['jobForID']),
    job() {
      return this.jobForID(this.jobId)
    },
    regularItems() {
      return this.loadList.filter(i => !this.isSeparator(i))
    },
    groups() {
      const map = {}
      for (const item of this.loadList) {
        const g = item.group_num
        if (!map[g]) map[g] = []
        map[g].push(item)
      }
      return Object.keys(map).sort((a, b) => Number(a) - Number(b)).map(k => map[k])
    },
    summary() {
      return {
        total: this.regularItems.length,
        pulled: this.regularItems.filter(i => i.pulled_at && !i.loaded_at).length,
        loaded: this.regularItems.filter(i => i.loaded_at && !i.returned_at).length,
        returned: this.regularItems.filter(i => i.returned_at).length
      }
    }
  },
  methods: {
    isSeparator(item) {
      return item.name.startsWith('-')
    },
    itemStatus(item) {
      if (item.returned_at) return 'returned'
      if (item.loaded_at) return 'loaded'
      if (item.pulled_at) return 'pulled'
      return 'none'
    },
    canUndo(item) {
      const lastAt = item.returned_at || item.loaded_at || item.pulled_at
      if (!lastAt) return false
      return dayjs().diff(dayjs(lastAt), 'minute') < 60
    },
    formatDate(dt) {
      if (!dt) return ''
      return dayjs(dt).format('HH:mm DD/MM/YYYY')
    },
    async addItem() {
      const stockItem = await this.$refs.addSelect.resolveAndClear()
      if (!stockItem) return
      const group_num = this.loadList.length > 0
        ? Math.max(...this.loadList.map(i => i.group_num))
        : 1
      await api.post(`/job/${this.jobId}/load-list`, {
        stock_item_id: stockItem.id,
        quantity: this.quantity,
        group_num
      })
      this.quantity = 1
      await this.fetchLoadList()
    },
    openEditGroup(item) {
      this.editGroupTarget = item
      this.editGroupName = item.name.slice(1)
      this.editGroupDialog = true
    },
    async saveGroupName() {
      if (!this.editGroupName.trim()) return
      await api.put(`/stock-item/${this.editGroupTarget.stock_item_id}`, {
        name: `-${this.editGroupName.trim()}`
      })
      this.editGroupDialog = false
      this.editGroupTarget = null
      await this.fetchLoadList()
    },
    openAddToGroup(groupNum) {
      this.addItemGroupNum = groupNum
      this.addItemDialog = true
    },
    async addToGroup() {
      const stockItem = await this.$refs.groupAddSelect.resolveAndClear()
      if (!stockItem) return
      await api.post(`/job/${this.jobId}/load-list`, {
        stock_item_id: stockItem.id,
        quantity: this.quantity,
        group_num: this.addItemGroupNum
      })
      this.quantity = 1
      this.addItemDialog = false
      await this.fetchLoadList()
    },
    async createGroup() {
      if (!this.newGroupName.trim()) return
      const nextGroupNum = this.loadList.length > 0
        ? Math.max(...this.loadList.map(i => i.group_num)) + 1
        : 1
      const stockItem = await api.post('/stock-item', { name: `-${this.newGroupName.trim()}` })
      await api.post(`/job/${this.jobId}/load-list`, {
        stock_item_id: stockItem.id,
        quantity: 1,
        group_num: nextGroupNum
      })
      this.newGroupName = ''
      this.newGroupDialog = false
      await this.fetchLoadList()
    },
    async pullItem(item) {
      await api.put(`/load-list-item/${item.id}/pull`)
      await this.fetchLoadList()
    },
    async loadItem(item) {
      await api.put(`/load-list-item/${item.id}/load`)
      await this.fetchLoadList()
    },
    async returnItem(item) {
      await api.put(`/load-list-item/${item.id}/return`)
      await this.fetchLoadList()
    },
    async undoItem(item) {
      await api.put(`/load-list-item/${item.id}/undo`)
      await this.fetchLoadList()
    },
    openEdit(item) {
      this.editTarget = item
      this.editDialog = true
    },
    confirmDelete(item) {
      this.deleteTarget = item
      this.deleteDialog = true
    },
    async removeItem() {
      await api.del(`/load-list-item/${this.deleteTarget.id}`)
      this.deleteDialog = false
      this.deleteTarget = null
      await this.fetchLoadList()
    },
    async fetchLoadList() {
      this.loadList = await api.get(`/job/${this.jobId}/load-list`)
    }
  },
  async created() {
    this.$store.dispatch('getJobs')
    await this.fetchLoadList()
  }
}
</script>

<style scoped lang="scss">
.add-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .item-search {
    flex: 1;
    min-width: 0;
  }

  .qty-field {
    flex: 0 0 50px;
  }
}

.load-table {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #888;
    padding: 6px 12px;
    border-bottom: 1px solid #e0e0e0;
  }

  td.action-col {
    width: 1px;
    white-space: nowrap;
    text-align: right;
  }

  td.icons-col {
    width: 60px;
  }

  td {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 0.875rem;
  }

  span.item {
    font-weight: bold;
    &:hover {
      cursor: pointer;
    }
  }

  .separator-cell {
    .group-name {
      &:hover { cursor: pointer; }
    }

    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #555;
    background: #f5f5f5;
    border-bottom: 1px solid #ddd;
    padding: 6px 12px;
  }

  .group-add-row td {
    padding: 2px 12px;
    border-bottom: 2px solid #e8e8e8;

    .v-icon {
      color: #bbb;
      &:hover {
        color: #555;
        cursor: pointer;
      }
    }
  }
}

@media (max-width: 600px) {
  .desktop-only {
    display: none;
  }
}
</style>
